<template>
  <section class="support-manage">
    <AppContentSkeleton v-if="loading" layout="block" label="Loading support fields" />
    <AppErrorState v-if="error" :message="error" icon-only @retry="load" />
    <div v-if="!loading && !loadFailed" class="support-manage__summary">
      <div><span>Systems</span><strong>{{ activeCount(systems) }}</strong></div>
      <div><span>Directions</span><strong>{{ activeCount(directions) }}</strong></div>
      <div><span>Modules</span><strong>{{ activeCount(modules) }}</strong></div>
      <div><span>Tags & responsibility</span><strong>{{ activeCount(tags) + activeCount(responsibleLabels) }}</strong></div>
    </div>

    <section v-if="!loading && !loadFailed" class="support-manage__section">
      <header class="support-manage__section-header">
        <div><h2>Systems, directions and modules</h2></div>
      </header>
      <app-button v-if="canEdit" design="new" @click.native="startCreate('systems')">Add system</app-button>

      <div class="support-manage__safe-note"><strong>Safe archive</strong><span>Values stay on existing tickets. A parent can be archived only after its active children are archived.</span></div>

      <div class="support-manage__tree">
        <create-row v-if="isCreating('systems')" singular="System" :draft="createDraft" :palette="palette" :working="creating" @select-color="createDraft.color = $event" @cancel="stopCreate" @submit="createItem" />

        <div v-for="system in ordered(systems)" :key="`system-${system.id}`" class="support-manage__branch">
          <manage-dictionary-row :readonly="!canEdit"
            :item="system"
            singular="System"
            context="Top-level product"
            expandable
            :expanded="isExpanded('systems', system.id)"
            :child-count="directionsFor(system.id).length"
            child-label="directions"
            add-child-label="Add direction"
            :working="isWorking('systems', system.id)"
            @toggle-expand="toggleExpanded('systems', system.id)"
            @add-child="startChildCreate('directions', system.id)"
            @update="updateItem('systems', system, $event)"
          />

          <div v-if="isExpanded('systems', system.id)" class="support-manage__children support-manage__children--directions">
            <create-row v-if="isCreating('directions', system.id)" singular="Direction" :draft="createDraft" :palette="palette" :working="creating" @select-color="createDraft.color = $event" @cancel="stopCreate" @submit="createItem" />
            <div v-for="direction in ordered(directionsFor(system.id))" :key="`direction-${direction.id}`" class="support-manage__branch">
              <manage-dictionary-row :readonly="!canEdit"
                :item="direction"
                singular="Direction"
                :context="`Inside ${system.name}`"
                expandable
                :expanded="isExpanded('directions', direction.id)"
                :child-count="modulesFor(direction.id).length"
                child-label="modules"
                add-child-label="Add module"
                :working="isWorking('directions', direction.id)"
                @toggle-expand="toggleExpanded('directions', direction.id)"
                @add-child="startChildCreate('modules', direction.id)"
                @update="updateItem('directions', direction, $event)"
              />

              <div v-if="isExpanded('directions', direction.id)" class="support-manage__children support-manage__children--modules">
                <create-row v-if="isCreating('modules', direction.id)" singular="Module" :draft="createDraft" :palette="palette" :working="creating" @select-color="createDraft.color = $event" @cancel="stopCreate" @submit="createItem" />
                <manage-dictionary-row :readonly="!canEdit"
                  v-for="module in ordered(modulesFor(direction.id))"
                  :key="`module-${module.id}`"
                  :item="module"
                  singular="Module"
                  :context="`Inside ${direction.name}`"
                  :working="isWorking('modules', module.id)"
                  @update="updateItem('modules', module, $event)"
                />
                <p v-if="!modulesFor(direction.id).length && !isCreating('modules', direction.id)" class="support-manage__empty">No modules yet. Add the first module for this direction.</p>
              </div>
            </div>
            <p v-if="!directionsFor(system.id).length && !isCreating('directions', system.id)" class="support-manage__empty">No directions yet. Add the first direction for this system.</p>
          </div>
        </div>
        <app-no-results v-if="!systems.length" design="new" small title="No systems yet" text="" />
      </div>
    </section>

    <section v-if="!loading && !loadFailed" class="support-manage__section">
      <header class="support-manage__section-header">
        <div><h2>Tags and responsibility</h2></div>
      </header>

      <div class="support-manage__label-grid">
        <section class="support-manage__label-panel">
          <header><div><h3>Tags</h3><span>{{ activeCount(tags) }} active</span></div></header>
          <app-button v-if="canEdit" design="new" @click.native="startCreate('tags')">Add tag</app-button>
          <create-row v-if="isCreating('tags')" singular="Tag" :draft="createDraft" :palette="palette" :working="creating" @select-color="createDraft.color = $event" @cancel="stopCreate" @submit="createItem" />
          <manage-dictionary-row :readonly="!canEdit" v-for="tag in ordered(tags)" :key="`tag-${tag.id}`" :item="tag" singular="Tag" context="Ticket tag" :working="isWorking('tags', tag.id)" @update="updateItem('tags', tag, $event)" />
          <p v-if="!tags.length && !isCreating('tags')" class="support-manage__empty">No tags yet.</p>
        </section>

        <section class="support-manage__label-panel">
          <header><div><h3>Responsibility</h3><span>{{ activeCount(responsibleLabels) }} active</span></div></header>
          <app-button v-if="canEdit" design="new" @click.native="startCreate('responsible_labels')">Add responsibility</app-button>
          <create-row v-if="isCreating('responsible_labels')" singular="Responsibility" :draft="createDraft" :palette="palette" :working="creating" @select-color="createDraft.color = $event" @cancel="stopCreate" @submit="createItem" />
          <manage-dictionary-row :readonly="!canEdit" v-for="label in ordered(responsibleLabels)" :key="`responsibility-${label.id}`" :item="label" singular="Responsibility" context="Ownership label" :working="isWorking('responsible_labels', label.id)" @update="updateItem('responsible_labels', label, $event)" />
          <p v-if="!responsibleLabels.length && !isCreating('responsible_labels')" class="support-manage__empty">No responsibility labels yet.</p>
        </section>
      </div>
    </section>

  </section>
</template>

<script>
import { getSupportTicketDictionary, patchSupportTicketDictionary, postSupportTicketDictionary } from '@/http'
import AppContentSkeleton from '@/components/AppContentSkeleton.vue'
import AppErrorState from '@/components/AppErrorState.vue'
import ManageDictionaryRow from './components/ManageDictionaryRow'
import { techSupportKey } from '@/utils/techSupportAccess'
import { apiError, normalizePage } from './ticketState'
import { SUPPORT_TAG_COLORS } from './components/tagPalette'

const CreateRow = {
  props: {
    singular: { type: String, required: true },
    draft: { type: Object, required: true },
    palette: { type: Array, required: true },
    working: Boolean
  },
  template: `
    <form class="support-manage-create" @submit.prevent="$emit('submit')">
      <div class="support-manage-create__top">
        <app-input v-model="draft.name" design="new" :placeholder="'New ' + singular.toLowerCase() + ' name'" autofocus />
        <app-button design="new" theme="success" type="submit" :disabled="working || !draft.name.trim()">{{ working ? 'Adding…' : 'Add' }}</app-button>
        <app-button design="new" theme="dark" type="button" @click.native="$emit('cancel')">Cancel</app-button>
      </div>
      <div class="support-manage-create__colors" role="list" :aria-label="singular + ' color'">
        <span>Color</span>
        <button v-for="color in palette" :key="color" type="button" :style="{ backgroundColor: color }" :class="{ 'is-selected': draft.color.toUpperCase() === color.toUpperCase() }" :aria-label="'Use color ' + color" @click="$emit('select-color', color)"></button>
      </div>
    </form>
  `
}

const collectionNames = {
  systems: 'systems',
  directions: 'directions',
  modules: 'modules',
  tags: 'tags',
  responsible_labels: 'responsibleLabels'
}

export default {
  name: 'SupportTicketManage',
  components: { AppContentSkeleton, AppErrorState, CreateRow, ManageDictionaryRow },
  data() {
    return {
      systems: [],
      directions: [],
      modules: [],
      tags: [],
      responsibleLabels: [],
      expandedSystems: [],
      expandedDirections: [],
      creatingKind: null,
      creatingParentId: null,
      createDraft: { name: '', color: SUPPORT_TAG_COLORS[7] },
      palette: SUPPORT_TAG_COLORS,
      loading: true,
      loadFailed: false,
      creating: false,
      workingKey: null,
      error: ''
    }
  },
  computed: {
    canEdit() { return this.$store.getters['permissions/can'](techSupportKey('support', 'edit')) }
  },
  created() { this.load() },
  methods: {
    load() {
      this.loading = true
      this.error = ''
      this.loadFailed = false
      return Promise.all(['systems', 'directions', 'modules', 'tags', 'responsible_labels'].map(kind => getSupportTicketDictionary(kind))).then(responses => {
        this.systems = normalizePage(responses[0].data)
        this.directions = normalizePage(responses[1].data)
        this.modules = normalizePage(responses[2].data)
        this.tags = normalizePage(responses[3].data)
        this.responsibleLabels = normalizePage(responses[4].data)
        if (!this.expandedSystems.length && this.systems.length) {
          const firstSystem = this.ordered(this.systems)[0]
          this.expandedSystems = [firstSystem.id]
          const firstDirection = this.ordered(this.directionsFor(firstSystem.id))[0]
          if (firstDirection) this.expandedDirections = [firstDirection.id]
        }
      }).catch(error => { this.loadFailed = true; this.error = apiError(error) }).finally(() => { this.loading = false })
    },
    activeCount(items) { return items.filter(item => item.active).length },
    ordered(items) { return [...items].sort((left, right) => Number(right.active) - Number(left.active) || left.name.localeCompare(right.name)) },
    directionsFor(systemId) { return this.directions.filter(item => item.system === systemId) },
    modulesFor(directionId) { return this.modules.filter(item => item.direction === directionId) },
    isExpanded(kind, id) { return (kind === 'systems' ? this.expandedSystems : this.expandedDirections).includes(id) },
    toggleExpanded(kind, id) {
      const field = kind === 'systems' ? 'expandedSystems' : 'expandedDirections'
      this[field] = this[field].includes(id) ? this[field].filter(value => value !== id) : [...this[field], id]
    },
    isCreating(kind, parentId = null) { return this.creatingKind === kind && this.creatingParentId === parentId },
    startCreate(kind) {
      if (!this.canEdit) return
      this.creatingKind = kind
      this.creatingParentId = null
      this.createDraft = { name: '', color: SUPPORT_TAG_COLORS[7] }
      this.error = ''
    },
    startChildCreate(kind, parentId) {
      if (!this.canEdit) return
      this.creatingKind = kind
      this.creatingParentId = parentId
      this.createDraft = { name: '', color: kind === 'modules' ? SUPPORT_TAG_COLORS[8] : SUPPORT_TAG_COLORS[7] }
      if (kind === 'directions' && !this.expandedSystems.includes(parentId)) this.expandedSystems.push(parentId)
      if (kind === 'modules' && !this.expandedDirections.includes(parentId)) this.expandedDirections.push(parentId)
      this.error = ''
    },
    stopCreate() { this.creatingKind = null; this.creatingParentId = null },
    createItem() {
      const name = this.createDraft.name.trim()
      if (!name || !this.creatingKind || this.creating) return
      const kind = this.creatingKind
      const parentId = this.creatingParentId
      const payload = { name, color: this.createDraft.color }
      if (kind === 'directions') payload.system = parentId
      if (kind === 'modules') payload.direction = parentId
      this.creating = true
      this.error = ''
      postSupportTicketDictionary(kind, payload).then(response => {
        this[collectionNames[kind]].push(response.data)
        this.stopCreate()
        this.$root.$emit('support-classifications-updated')
      }).catch(error => { this.error = apiError(error) }).finally(() => { this.creating = false })
    },
    isWorking(kind, id) { return this.workingKey === `${kind}-${id}` },
    updateItem(kind, item, payload) {
      if (!this.canEdit) return
      if (this.workingKey) return
      this.workingKey = `${kind}-${item.id}`
      this.error = ''
      patchSupportTicketDictionary(kind, item.id, payload).then(response => {
        Object.assign(item, response.data)
        this.$root.$emit('support-classifications-updated')
        this.$root.$emit('support-ticket-refresh')
      }).catch(error => { this.error = apiError(error) }).finally(() => { this.workingKey = null })
    }
  }
}
</script>

<style lang="scss" scoped>
.support-manage { display: grid; gap: $ui-space-16; }
.support-manage__summary { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 12px; }
.support-manage__summary > div { display: flex; align-items: center; justify-content: space-between; gap: 12px; min-height: 68px; padding: 14px 16px; border: 1px solid $ui-line; border-radius: 14px; background: $ui-surface; box-shadow: $ui-card-shadow; }
.support-manage__summary span { color: $ui-muted; font-size: $ui-font-size-metadata; font-weight: 600; }
.support-manage__summary strong { color: $ui-ink; font: 600 23px $ui-font-heading; }
.support-manage__section { padding: 20px; border: 1px solid $ui-line; border-radius: 16px; background: $ui-surface-soft; }
.support-manage__section-header { display: flex; align-items: flex-start; justify-content: space-between; gap: 20px; margin-bottom: 16px; }
.support-manage__section-header > div { display: grid; gap: 3px; }
.support-manage__section-header span { color: $ui-blue; font-size: 10px; font-weight: 800; letter-spacing: .07em; text-transform: uppercase; }
.support-manage__section-header h2 { margin: 0; color: $ui-ink; font: 600 20px $ui-font-heading; }
.support-manage__section-header p { max-width: 760px; margin: 0; color: $ui-muted; font-size: 13px; line-height: 1.45; }
.support-manage__safe-note { display: flex; gap: 9px; margin: $ui-space-16 0 14px; padding: 10px 13px; border: 1px solid rgba($ui-blue, .16); border-radius: 10px; background: $ui-blue-soft; color: $ui-muted; font-size: 12px; }
.support-manage__safe-note strong { color: $ui-blue; }
.support-manage__tree, .support-manage__branch { display: grid; gap: 9px; }
.support-manage__children { display: grid; gap: 9px; margin-left: 20px; padding: 9px 0 2px 18px; border-left: 2px solid rgba($ui-blue, .14); }
.support-manage__children--modules { border-left-color: rgba($ui-green, .2); }
.support-manage__empty { margin: 0; padding: 12px 14px; border: 1px dashed $ui-line-strong; border-radius: 10px; color: $ui-muted; font-size: 12px; }
.support-manage__label-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 14px; }
.support-manage__label-panel { display: grid; align-content: start; gap: 9px; min-width: 0; padding: 14px; border: 1px solid $ui-line; border-radius: 14px; background: $ui-surface; }
.support-manage__label-panel > .btn { justify-self: start; }
.support-manage__label-panel > header { display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 2px 2px 8px; }
.support-manage__label-panel > header > div { display: flex; align-items: baseline; gap: 8px; }
.support-manage__label-panel h3 { margin: 0; color: $ui-ink; font: 600 17px $ui-font-heading; }
.support-manage__label-panel header span { color: $ui-muted; font-size: 11px; }
.support-manage__label-panel header button { min-height: 34px; padding: 0 11px; border: 1px dashed $ui-line-strong; border-radius: 8px; background: $ui-surface; color: $ui-blue; cursor: pointer; font: 600 $ui-font-size-metadata $ui-font-body; }
.support-manage__error { position: sticky; z-index: 5; bottom: 16px; margin: 0; padding: 12px 14px; border-radius: 10px; background: #fff1f2; box-shadow: $ui-card-shadow; color: $ui-red; font-size: 13px; }
@media (max-width: 1050px) { .support-manage__summary { grid-template-columns: repeat(2, minmax(0, 1fr)); } .support-manage__label-grid { grid-template-columns: 1fr; } }
@media (max-width: 650px) {
  .support-manage__summary { grid-template-columns: 1fr; }
  .support-manage__section { padding: 14px; }
  .support-manage__section-header, .support-manage__safe-note { align-items: flex-start; flex-direction: column; }
  .support-manage__children { margin-left: 7px; padding-left: 10px; }
}
</style>

<style lang="scss">
.support-manage-create { display: grid; gap: 10px; padding: 12px; border: 1px dashed $ui-blue; border-radius: 12px; background: rgba($ui-blue, .035); }
.support-manage-create__top { display: grid; grid-template-columns: minmax(180px, 1fr) auto auto; align-items: center; gap: 8px; }
.support-manage-create__colors { display: flex; align-items: center; flex-wrap: wrap; gap: 7px; }
.support-manage-create__colors > span { margin-right: 3px; color: $ui-muted; font-size: 11px; font-weight: 700; text-transform: uppercase; }
.support-manage-create__colors button { width: 23px; height: 23px; padding: 0; border: 2px solid #fff; border-radius: 50%; cursor: pointer; box-shadow: 0 0 0 1px rgba(16, 24, 40, .14); }
.support-manage-create__colors button.is-selected { box-shadow: 0 0 0 2px $ui-ink; }
@media (max-width: 560px) { .support-manage-create__top { grid-template-columns: 1fr 1fr; } .support-manage-create__top > :first-child { grid-column: 1 / -1; } }
</style>
